'use server'

import { apiRequest, ApiResponse } from '../request'

export type AppointmentKind = 'CONSULTATION' | 'EXAM' | 'ORDER'

export interface PresenceStatus {
    id: number
    /** Chave do controle nativo; null nos personalizados. */
    key: string | null
    name: string
    color: string
    icon: string | null
    position: number
    isSystem: boolean
    active: boolean
}

export type PresenceEventType =
    | 'ARRIVED'
    | 'STATUS_CHANGED'
    | 'ROOM_CHANGED'
    | 'FINISHED'

export interface PresenceRoom {
    id: number
    name: string
    description?: string | null
}

/** Movimento no balcão: chegada, troca de controle, troca de sala, saída. */
export interface PresenceEvent {
    id: number
    type: PresenceEventType
    createdAt: string
    notes: string | null
    status: { id: number; name: string; color: string } | null
    room: PresenceRoom | null
    createdBy: { id: number; name: string } | null
}

export interface PresenceEntry {
    id: number
    statusId: number
    roomId: number | null
    room?: PresenceRoom | null
    clientId: number
    scheduledAt: string | null
    arrivedAt: string
    leftAt: string | null
    notes: string | null
    status: Pick<
        PresenceStatus,
        'id' | 'key' | 'name' | 'color' | 'icon' | 'position'
    >
    client: {
        id: number
        name: string
        cpf: string | null
        whatsAppNumber: string | null
        birthDate: string | null
    }
    consultation?: AppointmentSummary | null
    exam?: AppointmentSummary | null
    order?: AppointmentSummary | null
    createdBy?: { id: number; name: string } | null
    /** Só vem no histórico por agendamento. */
    events?: PresenceEvent[]
}

interface AppointmentSummary {
    id: number
    title: string | null
    status: string
    professional: { id: number; name: string } | null
}

export interface ScheduledAppointment {
    id: number
    kind: AppointmentKind
    title: string
    status: string
    scheduledAt: string | null
    arrivalOrder: boolean
    client: {
        id: number
        name: string
        cpf: string | null
        whatsAppNumber: string | null
    }
    professional: { id: number; name: string } | null
    /** Preenchido quando o agendamento já está no painel de presença. */
    presenceEntryId: number | null
}

export interface SearchAppointmentsParams {
    search?: string
    /** Tipos separados por vírgula. Vazio = todos. */
    kinds?: string
    startDate?: string
    endDate?: string
    /** Itens por página. */
    limit?: number
    /** Página, começando em 1. */
    page?: number
    includePresent?: boolean
}

export interface PaginatedAppointments {
    items: ScheduledAppointment[]
    total: number
    page: number
    limit: number
}

export interface CreatePresenceEntryData {
    statusId: number
    /** Presença é sempre de um agendamento — nunca avulsa. */
    kind: AppointmentKind
    appointmentId: number
    roomId?: number
    notes?: string
}

export interface UpdatePresenceEntryData {
    statusId?: number
    /** `null` desvincula a sala; omitir mantém a atual. */
    roomId?: number | null
    notes?: string
}

export interface CreatePresenceStatusData {
    name: string
    color?: string
    icon?: string
    position?: number
    active?: boolean
}

export type UpdatePresenceStatusData = Partial<CreatePresenceStatusData>

export async function getPresencePanel(): Promise<ApiResponse<PresenceEntry[]>> {
    return apiRequest<PresenceEntry[]>('presence', {})
}

export async function searchPresenceAppointments(
    params: SearchAppointmentsParams
): Promise<ApiResponse<PaginatedAppointments>> {
    return apiRequest<PaginatedAppointments>('presence/appointments', {
        params: {
            ...params,
            includePresent: params.includePresent ? 'true' : undefined,
        },
    })
}

export async function createPresenceEntry(
    data: CreatePresenceEntryData
): Promise<ApiResponse<PresenceEntry>> {
    return apiRequest<PresenceEntry>('presence', { data }, 'post')
}

export async function updatePresenceEntry(
    id: number,
    data: UpdatePresenceEntryData
): Promise<ApiResponse<PresenceEntry>> {
    return apiRequest<PresenceEntry>(`presence/${id}`, { data }, 'patch')
}

export async function finishPresenceEntry(
    id: number
): Promise<ApiResponse<PresenceEntry>> {
    return apiRequest<PresenceEntry>(`presence/${id}`, {}, 'delete')
}

export interface SearchPresenceHistoryParams {
    search?: string
    startDate?: string
    endDate?: string
    limit?: number
    page?: number
}

export interface PaginatedPresenceEntries {
    items: PresenceEntry[]
    total: number
    page: number
    limit: number
}

/** Passagens recentes pelo balcão, para achar como e onde alguém foi atendido. */
export async function searchPresenceHistory(
    params: SearchPresenceHistoryParams
): Promise<ApiResponse<PaginatedPresenceEntries>> {
    return apiRequest<PaginatedPresenceEntries>('presence/history', { params })
}

/** Histórico de presença de um agendamento, para a tela de detalhe dele. */
export async function getPresenceByAppointment(
    kind: AppointmentKind,
    appointmentId: number
): Promise<ApiResponse<PresenceEntry[]>> {
    return apiRequest<PresenceEntry[]>(
        `presence/appointment/${kind}/${appointmentId}`,
        {}
    )
}

export async function getPresenceStatuses(
    includeInactive = false
): Promise<ApiResponse<PresenceStatus[]>> {
    return apiRequest<PresenceStatus[]>('presence/statuses', {
        params: includeInactive ? { includeInactive: 'true' } : undefined,
    })
}

export async function createPresenceStatus(
    data: CreatePresenceStatusData
): Promise<ApiResponse<PresenceStatus>> {
    return apiRequest<PresenceStatus>('presence/statuses', { data }, 'post')
}

export async function updatePresenceStatus(
    id: number,
    data: UpdatePresenceStatusData
): Promise<ApiResponse<PresenceStatus>> {
    return apiRequest<PresenceStatus>(
        `presence/statuses/${id}`,
        { data },
        'patch'
    )
}

export async function reorderPresenceStatuses(
    ids: number[]
): Promise<ApiResponse<PresenceStatus[]>> {
    return apiRequest<PresenceStatus[]>(
        'presence/statuses/reorder',
        { data: { ids } },
        'patch'
    )
}

export async function deletePresenceStatus(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`presence/statuses/${id}`, {}, 'delete')
}

/** Rótulo de cada movimento na linha do tempo. */
export const PRESENCE_EVENT_LABELS: Record<PresenceEventType, string> = {
    ARRIVED: 'Chegou',
    STATUS_CHANGED: 'Trocou de controle',
    ROOM_CHANGED: 'Encaminhado para a sala',
    FINISHED: 'Saiu do painel',
}

/** Rótulo exibido para cada origem do agendamento. */
export const APPOINTMENT_KIND_LABELS: Record<AppointmentKind, string> = {
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    ORDER: 'Procedimento',
}

/** Classes do badge por cor semântica do controle. */
export const PRESENCE_COLOR_CLASSES: Record<string, string> = {
    error: 'badge-error',
    warning: 'badge-warning',
    accent: 'badge-accent',
    success: 'badge-success',
    info: 'badge-info',
    primary: 'badge-primary',
    secondary: 'badge-secondary',
    neutral: 'badge-neutral',
}

/** A cor é um hexadecimal escolhido na paleta, e não uma cor do tema. */
export function isCustomColor(color: string): boolean {
    return /^#[0-9a-fA-F]{6}$/.test(color)
}

/**
 * Preto ou branco sobre a cor escolhida, pelo brilho percebido (fórmula YIQ).
 *
 * Sem isso, a clínica que escolhe um amarelo claro na paleta fica com texto
 * branco em cima — ilegível justamente no controle que ela criou para chamar
 * atenção.
 */
export function contrastingTextColor(hex: string): string {
    const value = hex.replace('#', '')
    const r = parseInt(value.substring(0, 2), 16)
    const g = parseInt(value.substring(2, 4), 16)
    const b = parseInt(value.substring(4, 6), 16)
    return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#1f2937' : '#ffffff'
}

/**
 * Como pintar o badge de um controle: cor do tema vira classe, hexadecimal vira
 * estilo inline. Devolve os dois para o chamador espalhar no elemento.
 */
export function presenceBadgeProps(color: string): {
    className: string
    style?: { backgroundColor: string; color: string; borderColor: string }
} {
    if (isCustomColor(color)) {
        return {
            className: '',
            style: {
                backgroundColor: color,
                color: contrastingTextColor(color),
                borderColor: color,
            },
        }
    }
    return { className: PRESENCE_COLOR_CLASSES[color] ?? 'badge-neutral' }
}
